import React, { useEffect, useMemo } from 'react'

import type { MemberOnboardingInNetworkVerificationRecordUnknownNetworkStatusReason, MemberOnboardingProviderInNetworkMap, MemberOnboardingProviderInNetworkMapInNetwork } from 'utils/web_api.schemas'

import { InNetworkStatusIcon, InNetworkVerificationProgressBadge } from '../ProviderInNetworkVerification'
import { useProviderInNetworkVerificationContext } from '../ProviderInNetworkVerificationContext/ProviderInNetworkVerificationContext'

const UnknownReasonText: React.FC<{
  unknownReason?: MemberOnboardingInNetworkVerificationRecordUnknownNetworkStatusReason
}> = ({ unknownReason }) => {
  let label: string
  switch (unknownReason) {
    case 'CONFLICTING_RESULTS':
      label = "We weren't able to determine if this provider was in-network"
      break
    case 'UNABLE_TO_CONTACT_PRACTICE':
      label = "We tried calling this provider multiple times, but couldn't get through"
      break
    default:
      label = "We weren't able to determine if this provider was in-network"
  }

  if (!unknownReason) return null
  return <div className='body-sm text-gray-500'>{label}</div>
}

type ProviderStatus = {
  npi: number;
  name: string;
  unknownReason?: MemberOnboardingInNetworkVerificationRecordUnknownNetworkStatusReason;
}
const ProviderStatusLabels: React.FC<{
  status: MemberOnboardingProviderInNetworkMapInNetwork
  providers: ProviderStatus[]
}> = ({ status, providers }) => {
  return (
    <ul>
      {
        providers.map(provider => (
          <li key={provider.npi}>
            <div className='flex flex-row gap-1 items-center body-sm'>
              <InNetworkStatusIcon status={status} size='small' />
              {provider.name}
            </div>
            <UnknownReasonText unknownReason={provider.unknownReason} />
          </li>
        ))
      }
    </ul>
  )
}

const Divider: React.FC = () => {
  return <div className='w-full border-t bg-color-stroke-light last:hidden' />
}

export const useProviderInNetworkVerificationResults = ({
  planId,
  providerInNetworkMap,
  withDividers,
}: {
  planId: string;
  providerInNetworkMap?: MemberOnboardingProviderInNetworkMap[];
  withDividers?: boolean;
}): {
  canKickOffInNetworkVerificationTask: boolean;
  verifyingInNetworkStatusPlanIds: string[];
  status: MemberOnboardingProviderInNetworkMapInNetwork;
  label: string;
  badge: React.ReactNode;
  content: React.ReactNode;
} => {
  const { records, canKickOffInNetworkVerificationTask, verifyingInNetworkStatusPlanIds, inNetworkVerificationProgressStatus } = useProviderInNetworkVerificationContext()
  const { inNetworkProviders, outOfNetworkProviders, unknownProviders, status } = useMemo(() => {
    const inNetworkProviders: ProviderStatus[] = []
    const outOfNetworkProviders: ProviderStatus[] = []
    const unknownProviders: ProviderStatus[] = []
    providerInNetworkMap?.map(({ npi, name, in_network: inNetwork }) => {
      const matchingRecord = records.find(r => r.npi === npi && r.plan_id === planId)
      const result: ProviderStatus = { npi, name, unknownReason: matchingRecord?.unknown_network_status_reason }
      switch (matchingRecord?.in_network_status ?? inNetwork) {
        case 'IN_NETWORK':
          return inNetworkProviders.push(result)
        case 'OUT_OF_NETWORK':
          return outOfNetworkProviders.push(result)
        default:
          return unknownProviders.push(result)
      }
    })

    let status: MemberOnboardingProviderInNetworkMapInNetwork = 'UNKNOWN'
    if (outOfNetworkProviders.length) {
      status = 'OUT_OF_NETWORK'
    }
    if (inNetworkProviders.length) {
      status = 'IN_NETWORK'
    }
    return {
      inNetworkProviders,
      outOfNetworkProviders,
      unknownProviders,
      status,
    }
  }, [providerInNetworkMap, records, planId])

  const totalProvidersCount = providerInNetworkMap?.length ?? 0
  const label = `${inNetworkProviders.length ?? 0}/${totalProvidersCount} ${totalProvidersCount === 1 ? 'provider' : 'providers'} in-network`

  useEffect(() => {
    window.segment?.track('InNetworkProviderVerification - viewed')
  }, [])

  useEffect(() => {
    if (inNetworkVerificationProgressStatus) {
      window.segment?.track('InNetworkProviderVerification - status updated', {
        status: inNetworkVerificationProgressStatus
      })
    }
  }, [inNetworkVerificationProgressStatus])

  const badge = verifyingInNetworkStatusPlanIds.includes(planId)
    ? (
      <div>
        <InNetworkVerificationProgressBadge inNetworkVerificationProgressStatus={inNetworkVerificationProgressStatus} />
      </div>
      )
    : null

  return {
    canKickOffInNetworkVerificationTask,
    verifyingInNetworkStatusPlanIds,
    status,
    label,
    badge,
    content: (
      <div className='flex flex-col gap-2'>
        {
          inNetworkProviders.length > 0 && (
            <>
              <div className='body-sm-bold font-medium'>In-network</div>
              <ProviderStatusLabels status='IN_NETWORK' providers={inNetworkProviders} />
            </>
          )
        }
        {
          outOfNetworkProviders.length > 0 && (
            <>
              {withDividers && <Divider />}
              <div className='body-sm-bold font-medium'>Out-of-network</div>
              <ProviderStatusLabels status='OUT_OF_NETWORK' providers={outOfNetworkProviders} />
              <span className='body-small text-gray-500'>
                These providers said they were out-of-network with this plan
              </span>
            </>
          )
        }
        {
          unknownProviders.length > 0 && (
            <>
              {withDividers && <Divider />}
              <div className='body-sm-bold font-medium'>Unknown</div>
              <ProviderStatusLabels status='UNKNOWN' providers={unknownProviders} />
            </>
          )
        }
        {badge}
      </div>
    )
  }
}
